import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { IComponent } from '../../../typings/generic-component';
import { LIVE, MatchStatusList, MatchStatusType, PREMATCH } from '../../../typings/match';
import { getMatchStatusLabel, isLive, isNotEnded } from '../../../utils/match-status';
import { filterMatchTime, getMatchTime, IMatchTime } from '../../../utils/match-times';
import MatchStatus from '../../atoms/match-status/match-status';

export interface IMatchStatusWrapper extends IComponent {
  backgroundColor?: boolean;
  status: MatchStatusType;
  matchCurrentDateTime?: string;
  statusStartDateTime?: string;
  timerInterval?: number; // SECONDS, not MILLISECONDS
  externalInterval?: number; // Incrementing INTERVAL fires UPDATE CURRENT TIMING (can be INTERNAL using SETINTERVAL or from OUTSIDE, e.g. WEB-APP)
  maxStoppageMinutes?: number; // Maximum number of stoppage time to show in timing, e.g. 45+6' 1T
}

const MatchStatusWrapper: React.FC<IMatchStatusWrapper> = ({
  /* istanbul ignore next: default value */ backgroundColor = false,
  /* istanbul ignore next: default value */ status = PREMATCH,
  statusStartDateTime,
  matchCurrentDateTime,
  externalInterval, // AMOUNT of SECONDS to ADD to "matchCurrentDateTime"
  /* istanbul ignore next: default value */ timerInterval = 20, // SECONDS, not MILLISECONDS
  /* istanbul ignore next: default value */ maxStoppageMinutes = 6, // MINUTES, not SECONDS
  className,
}) => {
  const label: string | undefined = getMatchStatusLabel(status);
  const filteredMatchTime: IMatchTime[] = filterMatchTime(status);
  const live: boolean = isLive(status);
  const chrono: boolean = isLive(status) && status !== LIVE;
  const red: boolean = isNotEnded(status);

  // REF/STATE for RENDER with TIMER
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const [interval, updateInterval] = useState<number>(externalInterval ?? 0); // FIXME: 0 or null??? SECONDS passed from 1st RENDER of the COMPONENT

  // When to CREATE INTERNAL TIMER (AMP only)
  useEffect(() => {
    if (
      typeof statusStartDateTime !== 'undefined' &&
      typeof matchCurrentDateTime !== 'undefined' &&
      typeof externalInterval === 'undefined' &&
      filteredMatchTime.length > 0
    ) {
      // INTERNAL TIMER is used ONLY by the AMP VERSION, and <amp-script> runs in a WEB WORKER so it's OK
      timerRef.current = setInterval(
        () => updateInterval((value: number) => value + timerInterval),
        timerInterval * 1000
      );

      // onDestroy() callback
      return () => {
        updateInterval(0);

        if (timerRef.current) {
          // Just ONCE
          clearInterval(Number(timerRef.current));
          timerRef.current = undefined;
        }
      };
    } else {
      return () => {}; // Useless, only to avoid "error TS7030: Not all code paths return a value"
    }

    // Do NOT pass INTERVAL as a DEPENDENCY, or every TICK will DESTROY and RE-CREATE timer!
  }, [externalInterval, matchCurrentDateTime, status, statusStartDateTime, timerInterval]); // eslint-disable-line react-hooks/exhaustive-deps

  // When to USE EXTERNAL TIMER (e.g. the WebApp; not used anymore after the fix on 2021-09-13, now we pass updated "matchCurrentDateTime" prop to this component)
  useEffect(() => {
    if (
      typeof statusStartDateTime !== 'undefined' &&
      typeof matchCurrentDateTime !== 'undefined' &&
      typeof externalInterval !== 'undefined' &&
      filteredMatchTime.length > 0
    ) {
      updateInterval(externalInterval!);

      if (timerRef.current) {
        // Just ONCE
        clearInterval(Number(timerRef.current));
        timerRef.current = undefined;
      }
    }
  }, [externalInterval, matchCurrentDateTime, status, statusStartDateTime, timerInterval]); // eslint-disable-line react-hooks/exhaustive-deps

  const matchTime =
    statusStartDateTime && matchCurrentDateTime
      ? getMatchTime({
          matchStatusType: status,
          dateFrom: statusStartDateTime,
          dateTo: matchCurrentDateTime,
          interval,
          unit: 'seconds',
          maxStoppageMinutes, // MINUTES, not SECONDS
        })
      : undefined;

  return (
    <React.Fragment>
      {label && (
        <MatchStatus
          label={label}
          backgroundColor={backgroundColor}
          live={live}
          chrono={chrono}
          red={red}
          matchTime={matchTime}
          className={className}
        />
      )}
    </React.Fragment>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  MatchStatusWrapper.propTypes = {
    backgroundColor: PropTypes.bool,
    className: PropTypes.string,
    externalInterval: PropTypes.number,
    matchCurrentDateTime: PropTypes.string,
    maxStoppageMinutes: PropTypes.number,
    status: PropTypes.oneOf(MatchStatusList).isRequired,
    statusStartDateTime: PropTypes.string,
    timerInterval: PropTypes.number,
  };
}

export default MatchStatusWrapper;
